export const formatOgImage = (imageURL: string) => {
  if (!imageURL) return '';
  const cdnUrl = imageURL.replace(/www\./, 'cdn.');
  try {
    const urlObj = new URL(cdnUrl);
    return `${urlObj.origin}/cdn-cgi/image/width=1200,height=800,fit=crop${urlObj.pathname}`;
  } catch {
    return `/cdn-cgi/image/width=1200,height=800,fit=crop${cdnUrl.startsWith('/') ? '' : '/'}${cdnUrl}`;
  }
};

export interface ImageSize {
  url: string;
  width: number;
  height: number;
}

export const extractImageSize = (
  image: { sizes?: Record<string, ImageSize> } | undefined,
  size?: string,
): ImageSize | undefined => {
  if (size && image?.sizes?.[size]) {
    return image.sizes[size];
  }
  return image?.sizes?.original ?? image?.sizes?.medium ?? image?.sizes?.medium_large ?? image?.sizes?.large;
};

export type CloudflareImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
  crop?: string;
  trim?: string;
  cloudflare?: string;
  resizeViaQueryParams?: boolean;
};

const BENZINGA_IMAGE_HOSTS = new Set([
  'cdn.benzinga.com',
  'www.benzinga.com',
  'benzinga.com',
  'cdnwp-s3.benzinga.com',
]);

export function buildCloudflareImageUrl(src: string, options: string): string {
  const baseUrl = src.split('?')[0];
  if (baseUrl.includes('/cdn-cgi/image/')) return src;
  if (!/^https?:\/\//i.test(baseUrl)) return src;

  try {
    const urlObj = new URL(baseUrl);
    if (urlObj.hostname === 'logo-cdn.benzinga.com') return src;
    if (!BENZINGA_IMAGE_HOSTS.has(urlObj.hostname)) return src;
    return `${urlObj.origin}/cdn-cgi/image/${options}${urlObj.pathname}`;
  } catch {
    return src;
  }
}

export function cloudflareImageLoader(loaderProps: CloudflareImageLoaderProps): string {
  if (loaderProps.src.split(/[?#]/)[0].toLowerCase().endsWith('.svg')) {
    return loaderProps.src;
  }

  if (loaderProps.resizeViaQueryParams) {
    const baseUrl = loaderProps.src.split('?')[0];
    let url = `${baseUrl}?optimize=medium&auto=webp&width=${loaderProps.width}`;
    if (loaderProps.crop) url += `&crop=${loaderProps.crop}`;
    if (loaderProps.trim) url += `&trim=${loaderProps.trim}`;
    return url;
  }

  const segments: string[] = [`width=${loaderProps.width}`, 'format=auto', `quality=${loaderProps.quality ?? 85}`];

  if (loaderProps.crop) segments.push('fit=crop', 'gravity=auto');
  if (loaderProps.trim) segments.push(`trim=${loaderProps.trim.replace(/,/g, ';')}`);
  if (loaderProps.cloudflare) {
    for (const part of loaderProps.cloudflare.split(',')) {
      const trimmed = part.trim();
      if (trimmed) segments.push(trimmed);
    }
  }

  return buildCloudflareImageUrl(loaderProps.src, segments.join(','));
}
